import { lazy, Suspense, useEffect, useRef } from 'react'
import { RouterProvider } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/lib/auth'
import { useIsMobile } from '@/hooks/useIsMobile'
import { isCopilotoEnabled } from '@/lib/featureFlags'
import { SupabaseProvider, useSupabase } from '@/lib/supabase-context'
import { ThemeProvider } from '@/lib/theme'
import { Toaster } from '@/components/ui/sonner'
import { router } from './router'

// BrainHub Core Providers
import { OrganizacionProvider, WorkspaceProvider } from '@/core/providers'
import { NotificacionesProvider } from '@/core/providers/NotificacionesProvider'
import { AvisoVersionNueva } from '@/components/AvisoVersionNueva'
import { ChatProvider } from '@/core/providers/ChatProvider'
import { useConfigureUsuariosAuth } from '@/hooks/useUsuarios'

/** Configures Auth0 token getter for usuariosService globally */
function UsuariosAuthSetup() {
  useConfigureUsuariosAuth()
  return null
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

// ORR-173: el copiloto móvil se carga lazy (no infla el bundle de desktop)
const CopilotoContable = lazy(() => import('@/pages/CopilotoContable'))

const FullscreenLoader = () => (
  <div className="flex h-dvh items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
  </div>
)

/**
 * ORR-173 — En móvil (<768px) con el flag del copiloto activo, la app ES el chat:
 * fullscreen, sin MainLayout ni navegación. Desktop y usuarios sin flag usan el router normal.
 * Si no está autenticado, cae al router (que maneja el login).
 */
function RootGate() {
  const { isAuthenticated, isLoading, user, loginWithRedirect } = useAuth0()
  const { sessionExpired } = useSupabase()
  const isMobile = useIsMobile()
  const yaRedirigio = useRef(false)

  // ORR-236: si el refresh token murió, Auth0 sigue diciendo isAuthenticated (la caché de
  // localstorage sobrevive al token). Sin este guard las queries volvían vacías, los permisos
  // degradaban a ninguno y la app mostraba "Acceso Denegado" — un falso positivo de permisos
  // que se diagnostica mirando RBAC cuando en realidad hay que volver a loguearse.
  // El ref evita relanzar la redirección en cada render mientras el browser navega.
  useEffect(() => {
    if (!sessionExpired || yaRedirigio.current) return
    yaRedirigio.current = true
    void loginWithRedirect({
      appState: { returnTo: window.location.pathname + window.location.search },
    })
  }, [sessionExpired, loginWithRedirect])

  if (sessionExpired) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-4 text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        <p className="text-muted-foreground text-sm">
          Tu sesión expiró. Te estamos llevando al login...
        </p>
      </div>
    )
  }

  if (isLoading) return <FullscreenLoader />

  if (isMobile && isAuthenticated && isCopilotoEnabled(user)) {
    // Móvil + copiloto: la app ES el chat con IA. NO montar ChatProvider (el chat
    // interno del estudio): el copiloto no lo consume y dispararía al pedo
    // chat_listar_conversaciones, chat_miembros y dos canales Realtime. Ver ORR-173.
    return (
      <div className="h-dvh overflow-hidden overscroll-none">
        <Suspense fallback={<FullscreenLoader />}>
          <CopilotoContable mobile />
        </Suspense>
      </div>
    )
  }

  // Desktop / sin flag: app completa. ChatProvider envuelve el router para el
  // chat interno (presencia, mensajería, badge de no leídos) en todas las rutas.
  return (
    <ChatProvider>
      <RouterProvider router={router} />
    </ChatProvider>
  )
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SupabaseProvider>
          <QueryClientProvider client={queryClient}>
            <OrganizacionProvider>
              <UsuariosAuthSetup />
              <NotificacionesProvider>
                <WorkspaceProvider>
                  <RootGate />
                  <Toaster richColors position="top-right" />
                  <AvisoVersionNueva />
                </WorkspaceProvider>
              </NotificacionesProvider>
            </OrganizacionProvider>
          </QueryClientProvider>
        </SupabaseProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
